import { useMemo } from 'react';
import { sharesBillDiscount, type BillDiscount, type CartLineItem } from '@/stores/use-cart-store';
import { computeLineGst, roundBillTotal } from '@/lib/gst';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { cashRoundingRuleFor } from '@/lib/cash-rounding';
import { useLocationStore } from '@/stores/use-location-store';

export interface BillTotals {
  /** Sum of unitPrice × quantity across all lines (paise) */
  subtotal: number;
  /** Sum of per-line discount amounts (paise) */
  totalDiscount: number;
  /** Bill-level discount applied on top of per-line discounts (paise). 0 when none. */
  totalBillDiscount: number;
  cgst: number;
  sgst: number;
  igst: number;
  taxTotal: number;
  roundOff: number;
  grandTotal: number;
  itemCount: number;
  /** True when the entered bill discount was clamped to the taxable base. */
  billDiscountClamped: boolean;
  /**
   * Per-line share of the bill-level discount, in paise, in the same order
   * as `items`. Each entry is `>= 0` and the sum equals `totalBillDiscount`.
   * All zeroes when there is no bill-level discount.
   */
  perLineBillDiscount: number[];
  /**
   * Tax grouped by GST/VAT rate, in first-seen order — one entry per slab so
   * the summary can label each rate instead of a blended one (QA T1-003).
   */
  taxSlabs: TaxSlabTotal[];
}

export interface TaxSlabTotal {
  rate: number;
  taxableAmount: number;
  cgst: number;
  sgst: number;
  igst: number;
}

/**
 * Compute the bill-level discount amount in paise from a user-entered
 * PERCENTAGE (whole %, 0..100) or FLAT (paise) discount, clamped to the
 * post-line-discount pre-GST taxable base.
 */
function computeBillDiscountAmount(
  discount: BillDiscount,
  taxableBase: number,
): number {
  if (taxableBase <= 0) return 0;
  if (discount.type === 'PERCENTAGE') {
    const clampedPct = Math.max(0, Math.min(100, discount.value));
    return Math.floor((taxableBase * clampedPct) / 100);
  }
  // FLAT
  const raw = Math.max(0, Math.floor(discount.value));
  return Math.min(raw, taxableBase);
}

/**
 * Pro-rate a bill-level discount across line items weighted by each line's
 * post-line-discount pre-GST value, then re-floor GST per line.
 *
 * Invariant: `sum(per-line tax after pro-ration) === billTotals.taxTotal`.
 * We never subtract the discount from the grand total after GST — that
 * would break the GSTR-1 export (per-line tax must match bill-level tax).
 *
 * Residual paise (from Math.floor rounding) are added to the first line
 * with a non-zero weight so the pro-rated sum matches the target exactly.
 */
export function useBillCalculations(
  items: CartLineItem[],
  billDiscount: BillDiscount | null = null,
  isInterState = false,
  /** Exchange credit + quote down payment the server settles first (PS2-001). */
  priorCredits = 0,
): BillTotals {
  // Tenant currency → tax regime + cash-rounding subunit, so the online cart
  // matches the offline path and the server on GCC tenants (QA 3.10.21).
  const currency = useTenantCurrency();
  // The rule can differ per location (T07), so a location switch recomputes.
  const locationId = useLocationStore((s) => s.currentLocationId);
  return useMemo(() => {
    if (items.length === 0) {
      return {
        subtotal: 0,
        totalDiscount: 0,
        totalBillDiscount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        taxTotal: 0,
        roundOff: 0,
        grandTotal: 0,
        itemCount: 0,
        billDiscountClamped: false,
        perLineBillDiscount: [],
        taxSlabs: [],
      };
    }

    const subtotal = items.reduce((sum, i) => sum + i.lineSubtotal, 0);
    const totalDiscount = items.reduce((sum, i) => sum + i.discountAmount, 0);

    // Taxable base = sum of per-line (subtotal - line discount) = pre-GST
    // pre-bill-discount value that GST is normally computed against. A
    // voucher for some items (C03) counts only its matching lines.
    const discountWeight = (i: CartLineItem) =>
      sharesBillDiscount(i.productId, billDiscount) ? i.lineSubtotal - i.discountAmount : 0;
    const taxableBase = items.reduce((sum, i) => sum + discountWeight(i), 0);

    // ── Bill-level discount (pro-rated) ────────────────────────────────
    let totalBillDiscount = 0;
    let billDiscountClamped = false;
    let perLineBillDiscount: number[] = items.map(() => 0);

    if (billDiscount && taxableBase > 0) {
      const requested =
        billDiscount.type === 'PERCENTAGE'
          ? Math.floor((taxableBase * Math.max(0, Math.min(100, billDiscount.value))) / 100)
          : Math.max(0, Math.floor(billDiscount.value));

      totalBillDiscount = Math.min(requested, taxableBase);
      billDiscountClamped = requested > taxableBase;

      if (totalBillDiscount > 0) {
        perLineBillDiscount = items.map((i) => {
          const w = discountWeight(i);
          if (w <= 0) return 0;
          return Math.floor((totalBillDiscount * w) / taxableBase);
        });
        // Distribute the rounding residual to the first line with a
        // non-zero taxable weight so sum(perLineBillDiscount) === total.
        const allocated = perLineBillDiscount.reduce((s, v) => s + v, 0);
        const residual = totalBillDiscount - allocated;
        if (residual > 0) {
          for (let idx = 0; idx < items.length; idx++) {
            if (discountWeight(items[idx]) > 0) {
              perLineBillDiscount[idx] += residual;
              break;
            }
          }
        }
      }
    }

    // ── Re-compute GST per line with the pro-rated discount subtracted ──
    // CLAUDE.md rule: tax is floored per line item, never rounded at the
    // bill level — keeps the GSTR-1 invariant line-tax == bill-tax. Uses
    // the same `computeLineGst` helper as the cart store so the floor
    // semantics stay in lock-step across all callers.
    let cgst = 0;
    let sgst = 0;
    let igst = 0;
    const slabsByRate = new Map<number, TaxSlabTotal>();

    // Product `price` is always the tax-EXCLUSIVE base; tax is computed on it
    // and added to the total. The tenant "show tax inclusive" flag only changes
    // how prices are displayed (see useTaxInclusive), never the payable total.
    items.forEach((item, idx) => {
      const lineTaxable = Math.max(
        0,
        item.lineSubtotal - item.discountAmount - perLineBillDiscount[idx],
      );
      const split = computeLineGst(lineTaxable, item.gstRate, isInterState, currency);
      cgst += split.cgst;
      sgst += split.sgst;
      igst += split.igst;
      const slab = slabsByRate.get(item.gstRate) ?? {
        rate: item.gstRate,
        taxableAmount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
      };
      slab.taxableAmount += lineTaxable;
      slab.cgst += split.cgst;
      slab.sgst += split.sgst;
      slab.igst += split.igst;
      slabsByRate.set(item.gstRate, slab);
    });

    const taxTotal = cgst + sgst + igst;
    // Tax is always added to reach the payable total (price is the exclusive base).
    const rawTotal = subtotal - totalDiscount - totalBillDiscount + taxTotal;
    // Cash rounding by the till location's rule (T07), on what is actually
    // handed over: the bill less any exchange credit / down payment (PS2-001).
    const netRaw = Math.max(0, rawTotal - priorCredits);
    const roundOff = roundBillTotal(netRaw, cashRoundingRuleFor(currency, locationId)) - netRaw;
    const grandTotal = rawTotal + roundOff;
    const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

    return {
      subtotal,
      totalDiscount,
      totalBillDiscount,
      cgst,
      sgst,
      igst,
      taxTotal,
      roundOff,
      grandTotal,
      itemCount,
      billDiscountClamped,
      perLineBillDiscount,
      taxSlabs: [...slabsByRate.values()],
    };
  }, [items, billDiscount, isInterState, currency, locationId, priorCredits]);
}

// ─── Exported for tests ─────────────────────────────────────────────────────
export { computeBillDiscountAmount };
